import { createTheme } from '@mantine/core';
import type { MantineColorsTuple, CSSVariablesResolver } from '@mantine/core';

// ============================================================================
// COLOR PALETTES
// ============================================================================

// Primary brand color - eco green for energy/sustainability theme
const ecoGreen: MantineColorsTuple = [
  '#e7f9ed',
  '#d0f0de',
  '#a0e0bc',
  '#6dcf98',
  '#43c179',
  '#2bb665',
  '#1fb058',
  '#13994b',
  '#0a8842',
  '#007637',
];

// Semantic: informational actions, links, secondary highlights
const ecoBlue: MantineColorsTuple = [
  '#e7f5ff',
  '#d0ebff',
  '#a5d8ff',
  '#74c0fc',
  '#4dabf7',
  '#339af0',
  '#228be6',
  '#1c7ed6',
  '#1971c2',
  '#1864ab',
];

// Semantic: warnings, attention, in-progress states
const ecoAmber: MantineColorsTuple = [
  '#fff9db',
  '#fff3bf',
  '#ffec99',
  '#ffe066',
  '#ffd43b',
  '#fcc419',
  '#fab005',
  '#f59f00',
  '#f08c00',
  '#e67700',
];

// Semantic: errors, destructive actions, critical alerts
const ecoRed: MantineColorsTuple = [
  '#fff5f5',
  '#ffe3e3',
  '#ffc9c9',
  '#ffa8a8',
  '#ff8787',
  '#ff6b6b',
  '#fa5252',
  '#f03e3e',
  '#e03131',
  '#c92a2a',
];

// ============================================================================
// CUSTOM DESIGN TOKENS
// ============================================================================

const designTokens = {
  headerHeight: '64px',
  footerBg: '#f8f9fa',
  borderSubtle: '#dee2e6',
  shadowCard: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
  shadowHover: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
  transitionFast: '150ms ease',
  transitionNormal: '200ms ease',
};

// ============================================================================
// CSS VARIABLES RESOLVER
// ============================================================================

export const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    '--ec-header-height': theme.other.headerHeight,
    '--ec-shadow-card': theme.other.shadowCard,
    '--ec-shadow-hover': theme.other.shadowHover,
    '--ec-transition-fast': theme.other.transitionFast,
    '--ec-transition-normal': theme.other.transitionNormal,
  },
  light: {
    '--ec-header-bg': '#ffffff',
    '--ec-footer-bg': theme.other.footerBg,
    '--ec-border-subtle': theme.other.borderSubtle,
    '--ec-body-bg': '#ffffff',
  },
  dark: {
    '--ec-header-bg': '#1a1b1e',
    '--ec-footer-bg': '#141517',
    '--ec-border-subtle': '#373a40',
    '--ec-body-bg': '#1a1b1e',
  },
});

// ============================================================================
// THEME CONFIGURATION
// ============================================================================

export const theme = createTheme({
  primaryColor: 'ecoGreen',
  colors: {
    ecoGreen,
    ecoBlue,
    ecoAmber,
    ecoRed,
  },
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeight: '700',
  },
  defaultRadius: 'md',
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  breakpoints: {
    xs: '36em',
    sm: '48em',
    md: '62em',
    lg: '75em',
    xl: '88em',
  },
  other: designTokens,
  components: {
    // Form inputs
    Button: {
      defaultProps: {
        radius: 'md',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    Select: {
      defaultProps: {
        radius: 'md',
      },
    },

    // Layout & containers
    Paper: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
        withBorder: true,
      },
    },
    Modal: {
      defaultProps: {
        radius: 'md',
        centered: true,
        transitionProps: { transition: 'pop', duration: 200 },
      },
    },
    Drawer: {
      defaultProps: {
        transitionProps: { transition: 'slide-left', duration: 200 },
      },
    },

    // Feedback & info
    Badge: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Alert: {
      defaultProps: {
        radius: 'md',
      },
    },
    Notification: {
      defaultProps: {
        radius: 'md',
      },
    },
    Tooltip: {
      defaultProps: {
        withArrow: true,
        transitionProps: { transition: 'pop', duration: 150 },
      },
    },

    // Navigation & structure
    Menu: {
      defaultProps: {
        transitionProps: { transition: 'pop-top-right', duration: 150 },
      },
    },
    Tabs: {
      defaultProps: {
        radius: 'md',
      },
    },
    Stepper: {
      defaultProps: {
        radius: 'md',
      },
    },

    // Data display
    Table: {
      defaultProps: {
        highlightOnHover: true,
        withTableBorder: true,
        withColumnBorders: false,
      },
    },
  },
});
